import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { themes } from '../../utils/themes';

// Halloween Components
import HalloweenBackgroundGraphics from './halloween/HalloweenBackgroundGraphics';
import ChristmasBackgroundGraphics from './christmas/ChristmasBackgroundGraphics';
import DiwaliBackgroundGraphics from './diwali/DiwaliBackgroundGraphics';
import NewYearBackgroundGraphics from './newyear/NewYearBackgroundGraphics';
import ValentinesBackgroundGraphics from './valentines/ValentinesBackgroundGraphics';
import BlackFridayBackgroundGraphics from './blackfriday/BlackFridayBackgroundGraphics';
import EidBackgroundGraphics from './eid/EidBackgroundGraphics';
import SummerBackgroundGraphics from './summersale/SummerBackgroundGraphics';
import DanglingSpider from './halloween/DanglingSpider';
import HauntedPumpkin from './halloween/HauntedPumpkin';
import HalloweenFog from './halloween/HalloweenFog';
import BloodMoon from './halloween/BloodMoon';
import InteractiveGhostParticles from './halloween/InteractiveGhostParticles';

// Christmas Components
import InteractiveFairyLights from './christmas/InteractiveFairyLights';
import HolidayGiftBox from './christmas/HolidayGiftBox';
import InteractiveSnow from './christmas/InteractiveSnow';

// New Year Components
import InteractiveFireworks from './newyear/InteractiveFireworks';
import ChampagneCelebration from './newyear/ChampagneCelebration';
import InteractiveBalloons from './newyear/InteractiveBalloons';

// Valentines Components
import CupidLetter from './valentines/CupidLetter';
import InteractiveHearts from './valentines/InteractiveHearts';

// Diwali Components
import DiyaLampMascot from './diwali/DiyaLampMascot';
import InteractiveDiyas from './diwali/InteractiveDiyas';
import FloatingSkyLanterns from './diwali/FloatingSkyLanterns';
import DiwaliCrackersShow from './diwali/DiwaliCrackersShow';
import RangoliMandala from './diwali/RangoliMandala';

const ThemeDecorator = () => {
    const { activeTheme } = useSelector((state) => state.ui);
    const [isLightningActive, setIsLightningActive] = useState(false);
    const [burstBats, setBurstBats] = useState([]);
    const [confettiBurst, setConfettiBurst] = useState(false);
    const [isMegaDhamaka, setIsMegaDhamaka] = useState(false);

    const currentTheme = themes[activeTheme] || themes.default;
    const { name, backgroundPattern } = currentTheme;

    // Halloween: Full-screen scare trigger
    const triggerHalloweenScare = useCallback(() => {
        setIsLightningActive(true);
        setTimeout(() => setIsLightningActive(false), 650);

        const bats = Array.from({ length: 14 }).map((_, i) => ({
            id: Date.now() + i,
            startX: 5 + Math.random() * 20,
            startY: 5 + Math.random() * 20,
            tx: 200 + Math.random() * 700,
            ty: -(300 + Math.random() * 550),
            rot: -35 + Math.random() * 70,
            duration: 1.4 + Math.random() * 0.8,
            delay: Math.random() * 0.35
        }));
        setBurstBats(bats);
        setTimeout(() => setBurstBats([]), 2600);
    }, []);

    // New Year: Confetti storm trigger
    const triggerConfettiStorm = useCallback(() => {
        setConfettiBurst(true);
        setTimeout(() => setConfettiBurst(false), 3000);
    }, []);

    // Diwali: Mega Dhamaka multi-cracker celebration trigger
    const triggerDiwaliMegaDhamaka = useCallback(() => {
        setIsMegaDhamaka(true);
        setTimeout(() => setIsMegaDhamaka(false), 3200);
    }, []);

    const renderThemeContent = () => {
        switch (name) {
            case 'Halloween':
                return (
                    <>
                        <HalloweenBackgroundGraphics />
                        <HalloweenFog
                            isLightningActive={isLightningActive}
                            burstBats={burstBats}
                            onTriggerScare={triggerHalloweenScare}
                        />
                        <DanglingSpider />
                        <BloodMoon onTriggerScare={triggerHalloweenScare} />
                        <HauntedPumpkin onTriggerScare={triggerHalloweenScare} />
                        <InteractiveGhostParticles />
                    </>
                );

            case 'Christmas':
                return (
                    <>
                        <ChristmasBackgroundGraphics />
                        <InteractiveFairyLights />
                        <HolidayGiftBox />
                        <InteractiveSnow />
                    </>
                );

            case 'New Year':
                return (
                    <>
                        <NewYearBackgroundGraphics />
                        <InteractiveFireworks />
                        <ChampagneCelebration onTriggerConfettiStorm={triggerConfettiStorm} />
                        <InteractiveBalloons />
                        {confettiBurst && (
                            <div className="fixed inset-0 pointer-events-none z-[74] overflow-hidden">
                                {Array.from({ length: 35 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="absolute top-[-20px] animate-confetti text-lg select-none"
                                        style={{
                                            left: `${Math.random() * 100}%`,
                                            animationDuration: `${2 + Math.random() * 2.5}s`,
                                            animationDelay: `${Math.random() * 0.5}s`,
                                        }}
                                    >
                                        {['🎉', '✨', '⭐', '🎊', '🥂'][i % 5]}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                );

            case 'Valentines':
                return (
                    <>
                        <ValentinesBackgroundGraphics />
                        <CupidLetter />
                        <InteractiveHearts />
                    </>
                );

            case 'Diwali':
                return (
                    <>
                        <DiwaliBackgroundGraphics />
                        <InteractiveDiyas />
                        <DiyaLampMascot onTriggerAnarFountain={triggerDiwaliMegaDhamaka} />
                        <FloatingSkyLanterns />
                        <DiwaliCrackersShow isMegaDhamaka={isMegaDhamaka} />
                        <RangoliMandala />
                    </>
                );

            case 'Black Friday':
                return (
                    <>
                        <BlackFridayBackgroundGraphics />
                    </>
                );

            case 'Eid':
                return (
                    <>
                        <EidBackgroundGraphics />
                    </>
                );

            case 'Summer Sale':
                return (
                    <>
                        <SummerBackgroundGraphics />
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <>
            {/* Background Pattern */}
            {activeTheme !== 'default' && backgroundPattern && (
                <div
                    className="fixed inset-0 z-[-1] transition-all duration-700 ease-in-out bg-cover bg-center"
                    style={{
                        backgroundImage: backgroundPattern,
                        opacity: 0.9
                    }}
                />
            )}

            {/* Interactive Theme Layer */}
            {renderThemeContent()}

        </>
    );
};

export default ThemeDecorator;
